/**
 * Task 4 - Product page checkout button logic.
 *
 * snippets/buy-buttons.liquid resolves one of three states per selected variant
 * and writes it to [data-product-state] on .buy-buttons-wrapper:
 *
 *   "preorder"   sold out, inventory policy = continue -> Pre order + checkout
 *   "in-stock"   buyable now                            -> Add to cart + checkout
 *   "sold-out"   sold out with no pre-order             -> wishlist only
 *   "unavailable" no such variant combination           -> wishlist only
 *
 * Checkout visibility is handled by CSS in that snippet. This file does the two
 * things CSS alone cannot:
 *
 *   1. Keeps [data-product-state] correct when the customer switches variant.
 *      Dawn refetches the section HTML on variant change, so the freshly
 *      rendered wrapper already carries the right state - we just copy it over.
 *   2. Suppresses the "Notify me" widget injected at runtime by the Notify Me
 *      app whenever the state is NOT sold-out. The app renders itself from its
 *      own script after page load, so it has to be caught with an observer
 *      rather than with Liquid.
 */
(function () {
  'use strict';

  // Confirmed from the live DOM. The Notify Me app on this store is
  // "restock-alerts" (it renders inside .nm-portal) and it does two awkward
  // things: it copies the theme's own .product-form__submit class onto its
  // buttons, and it sets inline `display: ... !important` on them. An inline
  // !important beats any stylesheet !important, which is why the CSS rules
  // alone could never hide "Buy it now".
  var NOTIFY_BUTTON_SELECTORS = [
    '.restock-alerts-notify-button',
    '.PRODUCT_PAGE-notify-button',
    '.notifyButtonStyle',
  ].join(',');

  // The same app ships its own wishlist button, duplicating ours.
  var APP_WISHLIST_SELECTORS = [
    '.nm-wishlist-button',
    '.nm-PRODUCT_PAGE-wishlist-button',
    'button.cad_save',
  ].join(',');

  // Our own wrapper around the payment button. The app rewrites
  // .shopify-payment-button's inline style back to block after we hide it, so
  // the wrapper - which it does not know about - is what we actually control.
  var CHECKOUT_SELECTORS = '.buy-buttons-checkout';

  // The value the wrapper is revealed with. It is a flex column so the payment
  // icons added for Task 11 can be ordered between the checkout button and
  // "More payment options"; forcing it back to `block` here would silently undo
  // that ordering, since this inline !important outranks the stylesheet.
  var CHECKOUT_VISIBLE_DISPLAY = 'flex';

  // The pre-order app on this store is Timesact: the button it injects is
  // .hengam-preorder-button (its markup is all "hengam-" prefixed) and its own
  // config points at "#tcustomizer-add".
  var PREORDER_TEXT = /pre[\s-]?order/i;

  var PREORDER_APP_SELECTORS = [
    '.hengam-preorder-button',
    '[class*="hengam-preorder" i]',
    '#tcustomizer-add',
    '[id*="tcustomizer-add" i]',
  ].join(',');

  /**
   * Bring the accelerated checkout back on a pre-order product.
   *
   * The pre-order app hides "Buy it now" for pre-orders in two ways: a class it
   * puts on .shopify-payment-button (its "hide buy it now button" setting), and
   * a support-written rule on this store that also flattens the unbranded
   * button with height:0 and scaleY(0). Both are stylesheet rules, so an inline
   * style is what actually overrides them - and all three properties have to be
   * reset, not just display, or the button comes back with no height.
   *
   * Only reset what is actually being suppressed, so this never fights Shopify
   * over the layout of a wallet button that is already visible.
   */
  function forceCheckoutVisible(root) {
    forceAll(CHECKOUT_SELECTORS, CHECKOUT_VISIBLE_DISPLAY, root);
    (root || document)
      .querySelectorAll('.buy-buttons-checkout .shopify-payment-button, .buy-buttons-checkout .shopify-payment-button__button')
      .forEach(function (el) {
        var cs = window.getComputedStyle(el);
        if (cs.display === 'none') el.style.setProperty('display', 'block', 'important');
        if (cs.visibility === 'hidden') el.style.setProperty('visibility', 'visible', 'important');
        if (parseFloat(cs.height) === 0) el.style.setProperty('height', 'auto', 'important');
        if (cs.transform && cs.transform !== 'none' && cs.transform.indexOf('matrix(1, 0, 0, 0') === 0) {
          el.style.setProperty('transform', 'none', 'important');
        }
      });
  }

  /** Force a display value past an app's inline !important. */
  function force(el, value) {
    if (!el) return;
    el.style.setProperty('display', value, 'important');
  }

  function forceAll(selector, value, root) {
    (root || document).querySelectorAll(selector).forEach(function (el) {
      force(el, value);
    });
  }

  var NOTIFY_SELECTORS = [
    '[id*="appikon" i]',
    '[class*="appikon" i]',
    '[id*="notify-me" i]',
    '[class*="notify-me" i]',
    '[id*="notify_me" i]',
    '[class*="notify_me" i]',
    '[class*="notifyme" i]',
    '[id*="back-in-stock" i]',
    '[class*="back-in-stock" i]',
    '[class*="back_in_stock" i]',
    '[class*="bis-button" i]',
    '[class*="bis_button" i]',
    '[data-notify-me]',
  ].join(',');

  // Text a Notify-me control is likely to carry. Used as a fallback when the
  // app's markup does not match any of the selectors above.
  var NOTIFY_TEXT = /(notify\s*me|notify\s+when|email\s+when\s+available|back\s+in\s+stock)/i;

  var HIDDEN_CLASS = 'js-notify-me-hidden';

  function wrappers() {
    return document.querySelectorAll('.buy-buttons-wrapper[data-product-state]');
  }

  function currentState() {
    var wrapper = document.querySelector('.buy-buttons-wrapper[data-product-state]');
    return wrapper ? wrapper.getAttribute('data-product-state') : null;
  }

  /**
   * Mirror the state onto <body> so app widgets rendered outside the buy-buttons
   * wrapper can still be targeted by the stylesheet.
   */
  function mirrorStateToBody(state) {
    if (!state) return;
    if (document.body.getAttribute('data-product-state') !== state) {
      document.body.setAttribute('data-product-state', state);
    }
  }

  function looksLikeNotifyControl(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.closest('.buy-buttons-wrapper style, script, template')) return false;

    var tag = el.tagName;
    if (tag !== 'BUTTON' && tag !== 'A' && tag !== 'INPUT' && tag !== 'SPAN' && tag !== 'DIV') {
      return false;
    }

    var text = (el.textContent || el.value || '').trim();
    // A control, not a whole page region: keep the text budget small so a
    // container that merely happens to contain the phrase is never hidden.
    if (text.length === 0 || text.length > 60) return false;
    return NOTIFY_TEXT.test(text);
  }

  /**
   * Find the Notify-me widgets and flag them. Flagging is idempotent and
   * reversible - the CSS decides whether a flagged node is actually hidden,
   * keyed off the current product state.
   */
  function flagNotifyWidgets(root) {
    var scope = root && root.nodeType === 1 ? root : document;
    var found = [];

    try {
      scope.querySelectorAll(NOTIFY_SELECTORS).forEach(function (el) {
        found.push(el);
      });
    } catch (e) {
      /* selector unsupported in this browser - fall through to the text pass */
    }

    // Text-based fallback, scoped to the product form area so the rest of the
    // page (footer links, policy pages) is never touched.
    var productArea = document.querySelector('.product__info-wrapper, .product, main');
    if (productArea) {
      productArea.querySelectorAll('button, a, input[type="button"], input[type="submit"]').forEach(function (el) {
        if (looksLikeNotifyControl(el)) found.push(el);
      });
    }

    found.forEach(function (el) {
      // Never flag our own buttons.
      if (el.classList.contains('product-form__submit')) return;
      if (el.closest('.shopify-payment-button')) return;
      el.classList.add(HIDDEN_CLASS);
    });
  }

  /**
   * The hidden name="id" input is rendered disabled when checkout is not
   * allowed. Dawn's updateVariantInputs only rewrites its value on variant
   * change, never the disabled attribute - so without this a product that first
   * rendered sold out would keep an inert input after the customer picked an
   * available variant, and Add to cart would post without a variant id.
   */
  function syncVariantInput(state) {
    var allow = state === 'in-stock' || state === 'preorder';
    document.querySelectorAll('.buy-buttons-wrapper input.product-variant-id').forEach(function (input) {
      if (allow) {
        input.removeAttribute('disabled');
      } else {
        input.setAttribute('disabled', 'disabled');
      }
    });
  }

  function isVisible(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.offsetParent === null && el.getClientRects().length === 0) return false;
    var cs = window.getComputedStyle(el);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0';
  }

  /**
   * The pre-order app's own "Pre-order now" button, if it has rendered one that
   * the customer can actually see.
   *
   * Judged on visibility, never on presence: the app leaves its button in the
   * DOM and hides it for variants it does not cover, and the theme's own button
   * has to stay in that case.
   */
  function visiblePreorderButton(root) {
    var scope = root || document;
    var found = null;
    var candidates;
    try {
      candidates = scope.querySelectorAll(PREORDER_APP_SELECTORS);
    } catch (e) {
      return null;
    }
    for (var i = 0; i < candidates.length; i++) {
      if (isOwnControl(candidates[i])) continue;
      if (isVisible(candidates[i])) {
        found = candidates[i];
        break;
      }
    }
    if (found) return found;

    // Fallback for a renamed or re-skinned app button: any other visible
    // control in our own button row that reads "Pre-order". It can only fire
    // when a second pre-order button is genuinely sitting next to ours, which
    // is the very thing being fixed. The accelerated checkout is excluded - it
    // also reads "Pre-order now" for a deferred purchase, and hiding it is
    // handled separately.
    var area = scope.querySelector ? scope.querySelector('.product-form__buttons') : null;
    if (!area) return null;
    var controls = area.querySelectorAll('button, a[href], input[type="button"], input[type="submit"]');
    for (var k = 0; k < controls.length; k++) {
      var el = controls[k];
      if (isOwnControl(el)) continue;
      var text = (el.textContent || el.value || '').trim();
      if (!text || text.length > 40) continue;
      if (!PREORDER_TEXT.test(text)) continue;
      if (isVisible(el)) return el;
    }
    return null;
  }

  /**
   * The app's pre-order button is a CLONE of ours: it copies our button's id,
   * its classes and its type, adds .hengam-preorder-button, and clicks the
   * original on our behalf. So id and class say nothing about who owns a
   * button - only the app's own marker class does, and it has to be tested
   * first, before any check that would otherwise claim the clone as ours.
   */
  function isAppPreorderButton(el) {
    if (!el || el.nodeType !== 1) return false;
    try {
      if (el.matches(PREORDER_APP_SELECTORS)) return true;
    } catch (e) {
      /* selector unsupported - fall through */
    }
    return typeof el.className === 'string' && /hengam/i.test(el.className);
  }

  /** Ours, or the accelerated checkout we render - never the app's button. */
  function isOwnControl(el) {
    if (!el) return true;
    if (isAppPreorderButton(el)) return false;
    if (el.id && el.id.indexOf('ProductSubmitButton-') === 0) return true;
    if (el.hasAttribute('data-wishlist-trigger')) return true;
    return !!el.closest('.buy-buttons-checkout, .shopify-payment-button');
  }

  /**
   * The theme's own submit button.
   *
   * Not `querySelector('[id^="ProductSubmitButton-"]')`: the app's clone
   * carries the same id (duplicated in the document), and whichever of the two
   * comes first in the DOM wins that query - so on a pre-order product we could
   * end up hiding the app's button and keeping ours, the exact opposite of what
   * is wanted. name="add" is ours alone; the clone never gets it.
   */
  function ownSubmitButton(wrapper) {
    var candidates = wrapper.querySelectorAll(
      'button[name="add"], [id^="ProductSubmitButton-"], .product-form__submit'
    );
    for (var i = 0; i < candidates.length; i++) {
      if (!isAppPreorderButton(candidates[i])) return candidates[i];
    }
    return null;
  }

  /**
   * The client's rule, enforced from the other direction:
   *
   *   "When Notify me is shown, no checkout or buy option may appear anywhere
   *    on that product."
   *
   * In states A and B we hide the Notify-me widget, so this should never fire.
   * But the widget is injected by an app whose markup we do not control, so if
   * it ever renders with markup our selectors miss, the page would show both a
   * Notify-me and a Buy button - exactly the contradiction reported.
   *
   * So: if a Notify-me control is genuinely visible, checkout is withdrawn,
   * whatever state we resolved. Withdrawing a buy button is recoverable; telling
   * a customer an item is unavailable while asking them to buy it is not.
   */
  function enforceNotifyExclusivity() {
    var visibleNotify = false;
    var notifyIsSubmit = false;
    var candidates = [];

    // The Notify Me app does not always inject a button of its own - on this
    // store it rewrites the theme's submit button in place, so the "Notify me"
    // a customer sees IS #ProductSubmitButton. That button therefore has to be
    // inspected, not skipped: skipping it was why a page could still show
    // "Notify me" and "Buy it now" together.
    document.querySelectorAll('.buy-buttons-wrapper .product-form__submit').forEach(function (submit) {
      if (isAppPreorderButton(submit)) return;
      var label = (submit.textContent || '').trim();
      if (NOTIFY_TEXT.test(label) && isVisible(submit)) {
        visibleNotify = true;
        notifyIsSubmit = true;
      }
    });

    try {
      Array.prototype.push.apply(candidates, document.querySelectorAll(NOTIFY_SELECTORS));
    } catch (e) {
      /* ignore unsupported selectors */
    }
    var productArea = document.querySelector('.product__info-wrapper, .product, main');
    if (productArea) {
      productArea.querySelectorAll('button, a, input[type="button"], input[type="submit"]').forEach(function (el) {
        if (looksLikeNotifyControl(el)) candidates.push(el);
      });
    }

    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.classList.contains('product-form__submit')) continue;
      if (el.closest('.shopify-payment-button')) continue;
      if (isVisible(el)) { visibleNotify = true; break; }
    }

    // Sold out judged from the DOM, not from the state attribute.
    //
    // Dawn rewrites the submit button on variant change (product-info.js ->
    // toggleSubmitButton) without touching our wrapper, so a customer who picks
    // a sold-out variant can end up with a disabled "Sold out" button while
    // data-product-state still says in-stock - and the state-driven CSS then
    // never fires. Reading the button itself cannot drift.
    var soldOutNow = false;
    document.querySelectorAll('.buy-buttons-wrapper .product-form__submit').forEach(function (submit) {
      if (isAppPreorderButton(submit)) return;
      var label = (submit.textContent || '').trim();
      if (submit.hasAttribute('disabled') || /^(sold\s*out|unavailable)$/i.test(label)) {
        soldOutNow = true;
      }
    });

    wrappers().forEach(function (wrapper) {
      if (soldOutNow) {
        wrapper.setAttribute('data-sold-out-now', 'true');
      } else {
        wrapper.removeAttribute('data-sold-out-now');
      }
      if (visibleNotify) {
        wrapper.setAttribute('data-notify-visible', 'true');
        // Withdraw checkout here and now, not on the next pass. This runs
        // after enforceButtonSet, which may have just forced the checkout
        // visible with an inline style - and an inline style out-ranks the
        // stylesheet rule keyed to the attribute we are setting on this line.
        // Without this the contradiction ("Notify me" beside "Buy it now")
        // could survive until the next mutation, which may never come.
        forceAll(CHECKOUT_SELECTORS, 'none', wrapper);
      } else {
        wrapper.removeAttribute('data-notify-visible');
      }
      // When the submit button itself has become the Notify-me control it must
      // stay clickable - it is the only way for the customer to register
      // interest. Only the checkout paths are withdrawn.
      if (notifyIsSubmit) {
        wrapper.setAttribute('data-notify-is-submit', 'true');
      } else {
        wrapper.removeAttribute('data-notify-is-submit');
      }
    });
    if (visibleNotify) {
      document.body.setAttribute('data-notify-visible', 'true');
    } else {
      document.body.removeAttribute('data-notify-visible');
    }
  }

  /**
   * Decide what each control should do, and write it as an inline style so an
   * app cannot out-specify us.
   *
   *   State A / B : Notify me hidden, submit + wishlist + checkout shown
   *   State C     : Notify me ONLY - submit, wishlist and checkout all hidden
   */
  /**
   * Remember an element's own inline display before we override it, so the
   * exact original can be put back later. Plain removeProperty() is not enough:
   * some app buttons ship with their own inline `display: none !important`
   * (Cowlendar's "Book now", for one) and clearing it would reveal them.
   */
  function hideForSoldOut(el) {
    if (!el || el.dataset.pbsHidden === 'true') return;
    el.dataset.pbsPrevDisplay = el.style.getPropertyValue('display');
    el.dataset.pbsPrevPriority = el.style.getPropertyPriority('display');
    el.dataset.pbsHidden = 'true';
    el.style.setProperty('display', 'none', 'important');
  }

  function restoreAfterSoldOut(el) {
    if (!el || el.dataset.pbsHidden !== 'true') return;
    var prev = el.dataset.pbsPrevDisplay;
    var priority = el.dataset.pbsPrevPriority;
    el.style.removeProperty('display');
    if (prev) el.style.setProperty('display', prev, priority || '');
    delete el.dataset.pbsHidden;
    delete el.dataset.pbsPrevDisplay;
    delete el.dataset.pbsPrevPriority;
  }

  function isWishlistTrigger(el) {
    if (!el || el.nodeType !== 1) return false;
    return Boolean(
      (el.matches && el.matches('[data-wishlist-trigger]')) || el.querySelector('[data-wishlist-trigger]')
    );
  }

  function isNotifyControl(el) {
    if (!el) return false;
    try {
      if (el.matches(NOTIFY_BUTTON_SELECTORS)) return true;
      if (el.querySelector(NOTIFY_BUTTON_SELECTORS)) return true;
    } catch (e) {
      /* selector unsupported - fall through to the text check */
    }
    var text = (el.textContent || '').trim();
    return text.length > 0 && text.length <= 60 && NOTIFY_TEXT.test(text);
  }

  /**
   * State C is defined by subtraction, not by a list of selectors.
   *
   * Chasing each app's class names one at a time kept leaving something behind,
   * because several apps inject into this area (restock-alerts, Cowlendar,
   * Globo Product Options, Customer Account Deluxe) and they copy the theme's
   * own classes onto their buttons. So: when the variant is sold out, every
   * direct child of .product-form__buttons is hidden EXCEPT the Notify me
   * control. Anything any app adds in future is covered automatically.
   */
  function enforceButtonSet(state) {
    document.querySelectorAll('.buy-buttons-wrapper').forEach(function (wrapper) {
      var submit = ownSubmitButton(wrapper);
      var area = wrapper.querySelector('.product-form__buttons');
      if (!area) return;

      var label = submit ? (submit.textContent || '').trim() : '';
      var soldOut =
        state === 'sold-out' ||
        state === 'unavailable' ||
        (submit && submit.hasAttribute('disabled')) ||
        /^(sold\s*out|unavailable)$/i.test(label);

      // The app ships a wishlist button that duplicates ours; never show it.
      forceAll(APP_WISHLIST_SELECTORS, 'none', wrapper);

      if (soldOut) wrapper.removeAttribute('data-app-preorder');

      Array.prototype.forEach.call(area.children, function (child) {
        if (soldOut) {
          // The theme's own submit button stays visible in state C. It is
          // rendered `disabled` and reads "Sold out", so it tells the customer
          // what is going on next to the Notify me control without offering any
          // way to buy - which is the part of the rule that matters.
          if (submit && (child === submit || child.contains(submit))) {
            restoreAfterSoldOut(child);
            child.style.setProperty('display', 'block', 'important');
          } else if (isWishlistTrigger(child)) {
            // Wishlist stays as well. Still never revealed while it carries
            // `hidden` - that attribute is how wireWishlist() records that the
            // wishlist app has not loaded and the button would do nothing.
            restoreAfterSoldOut(child);
            if (!child.hasAttribute('hidden')) {
              child.style.setProperty('display', 'flex', 'important');
            }
          } else if (isNotifyControl(child)) {
            restoreAfterSoldOut(child);
            child.style.setProperty('display', 'block', 'important');
            child.querySelectorAll(NOTIFY_BUTTON_SELECTORS).forEach(function (el) {
              el.style.setProperty('display', 'flex', 'important');
            });
          } else {
            hideForSoldOut(child);
          }
        } else {
          restoreAfterSoldOut(child);
          // Outside state C the Notify me control must not appear at all.
          if (isNotifyControl(child)) {
            child.style.setProperty('display', 'none', 'important');
          }
        }
      });

      if (!soldOut) {
        // Scope matters. The Notify Me app renders some of its controls outside
        // the buy-button wrapper, so hiding only inside it left a "Notify me"
        // sitting above the pre-order button. The main product's info column is
        // the right boundary: it covers everything the app puts on THIS
        // product, and stops short of the related-products cards further down,
        // whose own sold-out items are entitled to a Notify me of their own.
        var productScope = wrapper.closest('.product__info-wrapper') || wrapper;
        forceAll(NOTIFY_BUTTON_SELECTORS, 'none', productScope);

        // ---- One pre-order button, not two ----
        //
        // The pre-order app (Timesact) injects its own "Pre-order now" button
        // into this same container and expects to hide the theme's. It does
        // that with a rule keyed to a Cowlendar class whose random suffix is
        // regenerated (.cowlendar-add-to-cart-<id>), so the rule stops matching
        // and both buttons come back - the duplicate the customer sees. The
        // loop above also clears inline display on every child, which would
        // undo the app's own hiding even when its rule does match.
        //
        // So: when the app's button is actually on screen, it owns the sale -
        // it is the control that carries the pre-order selling plan, and the
        // theme's would add the item without one. Ours goes, and so does the
        // accelerated checkout, which would bypass the plan the same way (the
        // app hides .shopify-payment-button itself for the same reason).
        var appPreorder = visiblePreorderButton(wrapper);

        // The one rule that outranks everything else here: a customer is never
        // shown "Notify me" and a way to buy in the same view. Checked before
        // the checkout is forced back on, because forcing it uses an inline
        // style - which would out-rank the stylesheet rule that withdraws
        // checkout whenever a Notify me control turns out to be visible.
        var notifyShowing =
          wrapper.getAttribute('data-notify-visible') === 'true' ||
          document.body.getAttribute('data-notify-visible') === 'true';

        if (notifyShowing) {
          forceAll(CHECKOUT_SELECTORS, 'none', wrapper);
        } else if (appPreorder) {
          wrapper.setAttribute('data-app-preorder', 'true');
          force(submit, 'none');
          forceCheckoutVisible(wrapper);
        } else {
          wrapper.removeAttribute('data-app-preorder');
          forceAll(CHECKOUT_SELECTORS, CHECKOUT_VISIBLE_DISPLAY, wrapper);

          if (submit) {
            submit.style.removeProperty('display');

            // A buyable product always shows Add to cart. Several apps write
            // into this area and one of them leaving the theme's button hidden
            // produced the worst of the reported states: "Add to wishlist" and
            // "Buy with Apple Pay" on an in-stock product, with no way to add
            // it to the cart. Clearing our own inline style is not enough when
            // the button was hidden by an app's stylesheet, so the computed
            // value is what gets checked.
            if (window.getComputedStyle(submit).display === 'none') {
              submit.style.setProperty('display', 'block', 'important');
            }
          }
        }

        wrapper.querySelectorAll('[data-wishlist-trigger]').forEach(function (el) {
          el.style.removeProperty('display');
        });
      }
    });
  }

  function applyState(state) {
    if (!state) return;
    mirrorStateToBody(state);
    syncVariantInput(state);
    flagNotifyWidgets(document);
    enforceButtonSet(state);
    // Let the browser apply the classes we just set before judging visibility.
    window.requestAnimationFrame(enforceNotifyExclusivity);
  }

  /**
   * Copy the state from the section HTML Dawn just fetched for the new variant.
   */
  function syncFromFetchedHtml(html, sectionId) {
    if (!html) return;
    var source =
      html.querySelector('.buy-buttons-wrapper[data-section-id="' + sectionId + '"]') ||
      html.querySelector('.buy-buttons-wrapper[data-product-state]');
    if (!source) return;

    var state = source.getAttribute('data-product-state');
    if (!state) return;

    wrappers().forEach(function (wrapper) {
      wrapper.setAttribute('data-product-state', state);
    });
    applyState(state);
  }

  /**
   * Add to wishlist.
   *
   * The store's wishlist is provided by the Customer Account Deluxe app, whose
   * own button (.cad_save) is hidden by the theme. The heart icon next to the
   * product title already proxies clicks to it (assets/custom.js); this does the
   * same for the button in the buy-buttons area, so both stay in sync.
   *
   * The button ships with the `hidden` attribute and is only revealed once the
   * app button is actually on the page - a wishlist button that silently does
   * nothing is worse than no button at all.
   */
  function wireWishlist() {
    var triggers = document.querySelectorAll('[data-wishlist-trigger]');
    if (!triggers.length) return;

    var appButton = document.querySelector('.cad_save');
    triggers.forEach(function (button, index) {
      // Only ever reveal one. Apps on this page clone parts of the buy-button
      // area, which produced two identical "Add to wishlist" buttons.
      //
      // Shown in every state, sold-out included: a customer who cannot buy the
      // piece today can still save it.
      if (appButton && index === 0) {
        button.removeAttribute('hidden');
      } else {
        button.setAttribute('hidden', '');
      }

      if (button.dataset.wishlistBound === 'true') return;
      button.dataset.wishlistBound = 'true';
      button.addEventListener('click', function (event) {
        event.preventDefault();
        var target = document.querySelector('.cad_save');
        if (target) target.click();
      });
    });
  }

  function init() {
    applyState(currentState());
    wireWishlist();

    // The wishlist app renders after load, like the Notify Me one - re-check.
    var wishlistTries = 0;
    var wishlistTimer = window.setInterval(function () {
      wishlistTries += 1;
      wireWishlist();
      if (wishlistTries >= 10) window.clearInterval(wishlistTimer);
    }, 700);

    // The Notify Me app injects its buttons after load, already carrying inline
    // `display: ... !important`. They must be corrected BEFORE the browser
    // paints, or the customer sees "Buy it now" flash up and then vanish.
    //
    // A MutationObserver callback is a microtask: it runs after the DOM change
    // but before the next paint. So the fix is applied synchronously here.
    // requestAnimationFrame would be one frame too late - that was the flash.
    if (typeof MutationObserver === 'function') {
      var pendingHeavy = null;

      var observer = new MutationObserver(function (records) {
        // Cheap relevance check first: most mutations on the page are nothing
        // to do with the buy buttons, and this runs on every one of them.
        var relevant = false;
        for (var i = 0; i < records.length && !relevant; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (
              node.closest('.buy-buttons-wrapper, .nm-portal') ||
              node.querySelector(
                '.nm-portal, [class*="notify" i], .shopify-payment-button, .cad_save, [class*="hengam" i], [id*="tcustomizer" i]'
              )
            ) {
              relevant = true;
              break;
            }
          }
        }
        if (!relevant) return;

        // Synchronous - before paint, so nothing flashes.
        enforceButtonSet(currentState());
        wireWishlist();

        // The heavier text-scanning pass can wait for the next frame.
        if (pendingHeavy) return;
        pendingHeavy = window.requestAnimationFrame(function () {
          pendingHeavy = null;
          applyState(currentState());
        });
      });

      observer.observe(document.body, { childList: true, subtree: true });

      // Second line of defence: an app can rewrite the inline style of a node we
      // already hid (restock-alerts does exactly this to .shopify-payment-button,
      // putting `display: block !important` straight back). Watch the style
      // attribute of anything we hid and re-assert it.
      //
      // Guarded on the current computed value so this cannot ping-pong with the
      // app: if it is already none we do nothing, so the exchange ends after one
      // correction rather than looping.
      var styleGuard = new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var el = records[i].target;
          if (!el || el.nodeType !== 1) continue;
          if (el.dataset.pbsHidden !== 'true') continue;
          if (el.style.getPropertyValue('display') === 'none') continue;
          el.style.setProperty('display', 'none', 'important');
        }
      });
      styleGuard.observe(document.body, {
        attributes: true,
        attributeFilter: ['style'],
        subtree: true,
      });
      window.setTimeout(function () {
        styleGuard.disconnect();
      }, 30000);

      // Stop watching once the apps have settled, so we are not observing the
      // whole document for the rest of the session.
      window.setTimeout(function () {
        observer.disconnect();
        applyState(currentState());
      }, 30000);
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var data = (event && event.data) || {};
        syncFromFetchedHtml(data.html, data.sectionId);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
